/**
 * Helpers for the PO line-item metafield column config-key encoding.
 *
 * The backend stores enabled metafield columns in a single map keyed by a
 * resource-aware id so the SAME namespace.name can exist for both product-
 * and variant-scoped metafields without collision:
 *
 *   PRODUCT          → "<namespace>.<name>"           (legacy form / backward-compat)
 *   PRODUCTVARIANT   → "variant:<namespace>.<name>"
 *
 * These helpers are shared between the column-settings view and the PO detail
 * view so the encoding stays consistent on both sides of save/load + lookup.
 */

export type MetafieldOwnerResource = 'PRODUCT' | 'PRODUCTVARIANT';

const VARIANT_PREFIX = 'variant:';

/**
 * Build the config-map key the backend expects.
 *
 * Examples:
 *   buildMetafieldConfigKey('PRODUCT', 'custom', 'code')        → 'custom.code'
 *   buildMetafieldConfigKey('PRODUCTVARIANT', 'custom', 'code') → 'variant:custom.code'
 */
export function buildMetafieldConfigKey(
  ownerResource: MetafieldOwnerResource,
  namespace: string,
  name: string,
): string {
  const tail = `${namespace}.${name}`;
  return ownerResource === 'PRODUCTVARIANT' ? `${VARIANT_PREFIX}${tail}` : tail;
}

/**
 * Inverse of `buildMetafieldConfigKey` — split a config-map key into its
 * ownerResource and bare `<namespace>.<name>` parts.
 *
 * Unknown / falsy input → defaults to PRODUCT with an empty effectiveKey,
 * matching the backend's lenient parsing.
 */
export function parseMetafieldConfigKey(
  configKey: string | null | undefined,
): { ownerResource: MetafieldOwnerResource; effectiveKey: string } {
  if (typeof configKey !== 'string' || configKey.length === 0) {
    return { ownerResource: 'PRODUCT', effectiveKey: '' };
  }
  if (configKey.startsWith(VARIANT_PREFIX)) {
    return {
      ownerResource: 'PRODUCTVARIANT',
      effectiveKey: configKey.slice(VARIANT_PREFIX.length),
    };
  }
  return { ownerResource: 'PRODUCT', effectiveKey: configKey };
}

/**
 * Convenience: build the config-map key for a metafield definition object
 * that already carries its ownerResource (the shape returned by
 * `fetchMetafieldDefinitions`).
 */
export function metafieldDefId(def: {
  ownerResource: MetafieldOwnerResource;
  namespace: string;
  name: string;
}): string {
  return buildMetafieldConfigKey(def.ownerResource, def.namespace, def.name);
}
